import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GraduationCap, Plus, Search, BookOpen, Video, Clock,
  CheckCircle2, AlertTriangle, Users, ChevronLeft,
  Upload, Trash2, Play, ChevronRight,
  Settings, X, Save, Eye, Award, Loader2, Sparkles, Pencil
} from 'lucide-react';
import { FaMagic } from 'react-icons/fa';

// ==================== COMPONENTES AUXILIARES ====================

const Badge = ({ children, variant = 'default' }) => {
  const styles = {
    default: 'bg-gray-100 text-gray-700',
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-700',
    danger: 'bg-red-50 text-red-700',
    info: 'bg-blue-50 text-blue-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[variant]}`}>
      {children}
    </span>
  );
};

const ProgressBar = ({ value, className = '' }) => (
  <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
    <div
      className={`h-2 rounded-full transition-all ${value >= 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
      style={{ width: `${Math.min(value, 100)}%` }}
    />
  </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
    <Icon size={48} strokeWidth={1.5} />
    <p className="mt-3 text-lg font-medium text-gray-500">{title}</p>
    {description && <p className="mt-1 text-sm">{description}</p>}
  </div>
);

const getEmpleadoDisplayName = (empleado) => {
  if (!empleado) return '';
  if (empleado.nombre && empleado.nombre.trim()) return empleado.nombre.trim();
  // Segundo nombre y segundo apellido son solo referenciales
  return [empleado.primer_nombre, empleado.apellido]
    .filter(Boolean)
    .join(' ')
    .trim();
};

const resolveUploadUrl = (rawPath) => {
  if (!rawPath) return '';
  if (rawPath.startsWith('/api/uploads/')) return rawPath;
  return `/api/uploads/${String(rawPath).replace(/^\/+/, '')}`;
};

const withAuthToken = (url) => {
  const token = localStorage.getItem('token');
  return token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;
};

const getContenidoUnits = (contenido) => {
  if (!contenido) return 0;
  return contenido.tipo === 'slides' ? (contenido.slides_count || 0) : 1;
};

const normalizePreguntaForEdit = (p = {}, idx = 0) => {
  const opcionesBase = Array.isArray(p.opciones) ? [...p.opciones] : [];
  while (opcionesBase.length < 4) opcionesBase.push('');
  const opciones = opcionesBase.slice(0, 4).map(opt => (opt ?? '').toString());
  const respuesta = Number.isInteger(p.respuesta_correcta_idx) && p.respuesta_correcta_idx >= 0 && p.respuesta_correcta_idx <= 3
    ? p.respuesta_correcta_idx
    : 0;

  return {
    pregunta: (p.pregunta || '').toString(),
    opciones,
    respuesta_correcta_idx: respuesta,
    orden: p.orden || idx + 1,
  };
};

// ==================== VISTA PRINCIPAL ====================

const TalleresView = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'seguridad';

  const [view, setView] = useState('catalogo'); // catalogo, curso, admin-crear
  const [cursos, setCursos] = useState([]);
  const [cursoActivo, setCursoActivo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCursos = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/api/talleres/cursos');
      setCursos(data);
    } catch (err) {
      console.error('Error cargando cursos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCursos(); }, [fetchCursos]);

  const openCurso = async (cursoId) => {
    try {
      const { data } = await axiosInstance.get(`/api/talleres/cursos/${cursoId}`);
      setCursoActivo(data);
      setView('curso');
    } catch (err) {
      console.error(err);
    }
  };

  const cursosFiltrados = cursos.filter(c =>
    c.titulo.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <AnimatePresence mode="wait">
        {view === 'catalogo' && (
          <CatalogoView
            key="catalogo"
            cursos={cursosFiltrados}
            loading={loading}
            search={search}
            setSearch={setSearch}
            isAdmin={isAdmin}
            onOpenCurso={openCurso}
            onNewCurso={() => setView('admin-crear')}
          />
        )}
        {view === 'curso' && cursoActivo && (
          <CursoDetailView
            key="curso"
            curso={cursoActivo}
            isAdmin={isAdmin}
            onBack={() => { setView('catalogo'); fetchCursos(); }}
            onRefresh={() => openCurso(cursoActivo.id)}
          />
        )}
        {view === 'admin-crear' && (
          <CursoFormView
            key="admin-crear"
            onBack={() => { setView('catalogo'); fetchCursos(); }}
            onCreated={(curso) => { openCurso(curso.id); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== CATALOGO ====================

const CatalogoView = ({ cursos, loading, search, setSearch, isAdmin, onOpenCurso, onNewCurso }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
    {/* Header */}
    <div className="flex items-center justify-between mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <GraduationCap className="text-blue-600" size={28} />
          Talleres y Cursos
        </h1>
        <p className="text-sm text-gray-500 mt-1">Seguridad y PCP</p>
      </div>
      {isAdmin && (
        <div className="flex gap-2">
          <button onClick={onNewCurso} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            <Plus size={16} /> Nuevo Curso
          </button>
        </div>
      )}
    </div>

    {/* Search */}
    <div className="relative mb-6">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
      <input
        type="text"
        placeholder="Buscar cursos..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
      />
    </div>

    {/* Grid */}
    {loading ? (
      <div className="flex justify-center py-20">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    ) : cursos.length === 0 ? (
      <EmptyState icon={BookOpen} title="No hay cursos disponibles" description={isAdmin ? "Crea el primer curso para empezar" : "Aún no tienes cursos asignados"} />
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {cursos.map(curso => (
          <CursoCard key={curso.id} curso={curso} onClick={() => onOpenCurso(curso.id)} />
        ))}
      </div>
    )}
  </motion.div>
);

const CursoCard = ({ curso, onClick }) => {
  const progreso = curso.progreso;
  return (
    <motion.div
      whileHover={{ y: -2 }}
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
    >
      {/* Banner */}
      <div className="h-40 bg-gradient-to-br from-blue-500 to-indigo-600 relative overflow-hidden">
        {curso.banner_path ? (
          <img src={curso.banner_path} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="flex items-center justify-center h-full">
            <GraduationCap className="text-white/30" size={64} />
          </div>
        )}
        {progreso?.completado && (
          <div className="absolute top-3 right-3">
            <Badge variant="success">Completado</Badge>
          </div>
        )}
        {progreso?.vencido && (
          <div className="absolute top-3 right-3">
            <Badge variant="danger">Vencido</Badge>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 mb-1 line-clamp-2">{curso.titulo}</h3>
        {curso.descripcion && (
          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{curso.descripcion}</p>
        )}

        <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
          {curso.duracion_minutos && (
            <span className="flex items-center gap-1"><Clock size={13} /> {curso.duracion_minutos} min</span>
          )}
          <span className="flex items-center gap-1"><BookOpen size={13} /> {curso.total_slides} slides</span>
          {curso.total_contenidos > 0 && (
            <span className="flex items-center gap-1"><Video size={13} /> {curso.total_contenidos}</span>
          )}
        </div>

        {/* Barra de progreso si tiene asignación */}
        {progreso && !progreso.completado && (
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progreso</span>
              <span>{progreso.porcentaje}%</span>
            </div>
            <ProgressBar value={progreso.porcentaje} />
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ==================== DETALLE CURSO ====================

const CursoDetailView = ({ curso, isAdmin, onBack, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('contenido'); // contenido, quiz, asignaciones
  const [slideIndex, setSlideIndex] = useState(0);
  const [activeContent, setActiveContent] = useState(null);
  const [viewingSlides, setViewingSlides] = useState(false);

  // Admin: gestión de contenido y asignaciones
  const [showUpload, setShowUpload] = useState(false);
  const [showAsignar, setShowAsignar] = useState(false);

  const progreso = curso.mi_progreso;
  const [workspace, setWorkspace] = useState(
    isAdmin ? (progreso ? 'capacitacion' : 'administracion') : 'capacitacion'
  );
  const canManage = isAdmin && workspace === 'administracion';
  const enModoCapacitacion = !isAdmin || workspace === 'capacitacion';

  useEffect(() => {
    if (!isAdmin) return;
    if (workspace === 'capacitacion' && !progreso) {
      setWorkspace('administracion');
    }
  }, [isAdmin, workspace, progreso]);

  useEffect(() => {
    setActiveTab('contenido');
  }, [workspace, curso.id]);

  const handleDeleteContenido = async (id) => {
    if (!window.confirm('Eliminar este contenido?')) return;
    try {
      await axiosInstance.delete(`/api/talleres/contenidos/${id}`);
      onRefresh();
    } catch (err) { console.error(err); }
  };

  const getContenidoOffset = (contenidoId) => {
    const contenidos = curso.contenidos || [];
    const idx = contenidos.findIndex(c => c.id === contenidoId);
    if (idx <= 0) return 0;
    return contenidos.slice(0, idx).reduce((acc, c) => acc + getContenidoUnits(c), 0);
  };

  const reportProgress = async (progresoAbsoluto) => {
    if (!enModoCapacitacion || !progreso) return;
    try {
      await axiosInstance.put(`/api/talleres/cursos/${curso.id}/progreso`, { progreso: progresoAbsoluto });
    } catch (err) { console.error(err); }
  };

  // Visor de slides
  if (viewingSlides && activeContent) {
    return (
      <SlideViewer
        contenido={activeContent}
        cursoId={curso.id}
        initialSlide={slideIndex}
        onBack={() => { setViewingSlides(false); onRefresh(); }}
        onProgress={(slideNum) => {
          const offset = getContenidoOffset(activeContent.id);
          reportProgress(offset + slideNum);
        }}
      />
    );
  }

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      {/* Header */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors">
        <ChevronLeft size={18} /> Volver al catálogo
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{curso.titulo}</h1>
          {curso.descripcion && <p className="text-gray-500 mt-1">{curso.descripcion}</p>}
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
            {curso.duracion_minutos && <span className="flex items-center gap-1"><Clock size={14} /> {curso.duracion_minutos} min</span>}
            <span className="flex items-center gap-1"><BookOpen size={14} /> {curso.total_slides} slides</span>
            {curso.requiere_quiz && <Badge variant="info">Quiz requerido</Badge>}
            {curso.vigencia_dias && <Badge variant="warning">Vigencia: {curso.vigencia_dias} días</Badge>}
          </div>
        </div>
        {enModoCapacitacion && progreso?.completado && (
          <a
            href={withAuthToken(`/api/talleres/certificado/${progreso.id}`)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            <Award size={16} /> Certificado
          </a>
        )}
      </div>

      {isAdmin && (
        <div className="mb-4">
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            {progreso && (
              <button
                onClick={() => setWorkspace('capacitacion')}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  workspace === 'capacitacion'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Mi capacitación
              </button>
            )}
            <button
              onClick={() => setWorkspace('administracion')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                workspace === 'administracion'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Administración
            </button>
          </div>
        </div>
      )}

      {/* Progreso del empleado */}
      {enModoCapacitacion && progreso && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Mi progreso</span>
            <span className="text-sm text-gray-500">{progreso.slides_vistos}/{progreso.total_slides} slides</span>
          </div>
          <ProgressBar value={progreso.porcentaje} />
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
            {progreso.completado && <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 size={13} /> Completado</span>}
            {progreso.vencido && <span className="flex items-center gap-1 text-red-500"><AlertTriangle size={13} /> Vencido</span>}
            {progreso.vence_en && !progreso.vencido && <span>Vence: {new Date(progreso.vence_en).toLocaleDateString('es-VE')}</span>}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {[
          { id: 'contenido', label: 'Contenido', icon: BookOpen },
          ...(curso.requiere_quiz ? [{ id: 'quiz', label: 'Quiz', icon: CheckCircle2 }] : []),
          ...(canManage ? [{ id: 'asignaciones', label: 'Asignaciones', icon: Users }] : []),
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon size={15} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'contenido' && (
        <div>
          {canManage && (
            <div className="flex gap-2 mb-4">
              <button onClick={() => setShowUpload(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <Upload size={14} /> Subir Presentación
              </button>
              <button onClick={() => setShowUpload('video')} className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <Video size={14} /> Agregar Video
              </button>
            </div>
          )}

          {(!curso.contenidos || curso.contenidos.length === 0) ? (
            <EmptyState icon={BookOpen} title="Sin contenido" description={canManage ? "Sube una presentación o agrega un video" : "Este curso aún no tiene contenido"} />
          ) : (
            <div className="space-y-3">
              {curso.contenidos.map(cont => (
                <div key={cont.id} className="flex items-center justify-between bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-200 transition-colors">
                  <div className="flex items-center gap-3">
                    {cont.tipo === 'slides' ? <BookOpen className="text-blue-500" size={20} /> :
                     cont.tipo === 'video_youtube' ? <Play className="text-red-500" size={20} /> :
                     <Video className="text-purple-500" size={20} />}
                    <div>
                      <p className="font-medium text-gray-800">{cont.titulo}</p>
                      <p className="text-xs text-gray-400">
                        {cont.tipo === 'slides' ? `${cont.slides_count} slides` :
                         cont.tipo === 'video_youtube' ? 'Video YouTube' : 'Video MP4'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {cont.tipo === 'slides' && (
                      <button
                        onClick={() => { setActiveContent(cont); setSlideIndex(0); setViewingSlides(true); }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-sm hover:bg-blue-100 transition-colors"
                      >
                        <Eye size={14} /> Ver
                      </button>
                    )}
                    {cont.tipo === 'video_youtube' && (
                      <a href={cont.video_url} target="_blank" rel="noopener noreferrer"
                        onClick={() => {
                          const offset = getContenidoOffset(cont.id);
                          reportProgress(offset + 1);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100 transition-colors">
                        <Play size={14} /> Ver
                      </a>
                    )}
                    {cont.tipo === 'video_mp4' && (
                      <button
                        onClick={() => { setActiveContent(cont); setViewingSlides(false); }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-purple-50 text-purple-600 rounded-lg text-sm hover:bg-purple-100 transition-colors"
                      >
                        <Play size={14} /> Ver
                      </button>
                    )}
                    {canManage && (
                      <button onClick={() => handleDeleteContenido(cont.id)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Video MP4 player inline */}
          {activeContent && activeContent.tipo === 'video_mp4' && !viewingSlides && (
            <div className="mt-4 bg-black rounded-xl overflow-hidden">
              <video
                controls
                className="w-full max-h-[500px]"
                src={resolveUploadUrl(activeContent.video_path)}
                onEnded={() => {
                  const offset = getContenidoOffset(activeContent.id);
                  reportProgress(offset + 1);
                }}
              />
            </div>
          )}

          {/* Sinopsis del curso */}
          {(curso.sinopsis || canManage) && (
            <SinopsisSection curso={curso} canManage={canManage} onRefresh={onRefresh} />
          )}
        </div>
      )}

      {activeTab === 'quiz' && (
        <QuizView
          curso={curso}
          onRefresh={onRefresh}
          canManage={canManage}
          canAnswer={enModoCapacitacion}
          progreso={progreso}
        />
      )}

      {activeTab === 'asignaciones' && canManage && (
        <AsignacionesView curso={curso} onRefresh={onRefresh} showAsignar={showAsignar} setShowAsignar={setShowAsignar} />
      )}


      {/* Modal Upload */}
      {showUpload && (
        <UploadModal
          cursoId={curso.id}
          type={showUpload === 'video' ? 'video' : 'slides'}
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); onRefresh(); }}
        />
      )}
    </motion.div>
  );
};

// ==================== VISOR DE SLIDES ====================

const SlideViewer = ({ contenido, cursoId, initialSlide = 0, onBack, onProgress }) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const total = contenido.slides_count || 0;

  const slideUrl = (index) => {
    // pdftoppm genera slide-01.png, slide-02.png, etc.
    const num = String(index + 1).padStart(2, '0');
    return `${resolveUploadUrl(contenido.slides_dir)}/slide-${num}.png`;
  };

  const goTo = (index) => {
    if (index < 0 || index >= total) return;
    setCurrentSlide(index);
    onProgress(index + 1);
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') goTo(currentSlide + 1);
      if (e.key === 'ArrowLeft') goTo(currentSlide - 1);
      if (e.key === 'Escape') onBack();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-gray-900 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-gray-800">
        <button onClick={onBack} className="flex items-center gap-1 text-gray-300 hover:text-white text-sm transition-colors">
          <ChevronLeft size={18} /> Volver
        </button>
        <span className="text-gray-300 text-sm font-medium">{contenido.titulo}</span>
        <span className="text-gray-400 text-sm">{currentSlide + 1} / {total}</span>
      </div>

      {/* Slide */}
      <div className="flex-1 flex items-center justify-center relative px-16">
        <button onClick={() => goTo(currentSlide - 1)} disabled={currentSlide === 0}
          className="absolute left-4 p-2 bg-white/10 hover:bg-white/20 rounded-full disabled:opacity-30 transition-colors">
          <ChevronLeft className="text-white" size={24} />
        </button>

        <img
          key={currentSlide}
          src={slideUrl(currentSlide)}
          alt={`Slide ${currentSlide + 1}`}
          className="max-h-[calc(100vh-120px)] max-w-full object-contain rounded-lg shadow-2xl"
        />

        <button onClick={() => goTo(currentSlide + 1)} disabled={currentSlide >= total - 1}
          className="absolute right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full disabled:opacity-30 transition-colors">
          <ChevronRight className="text-white" size={24} />
        </button>
      </div>

      {/* Progress bar */}
      <div className="px-6 py-3 bg-gray-800">
        <div className="w-full bg-gray-700 rounded-full h-1.5">
          <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${((currentSlide + 1) / total) * 100}%` }} />
        </div>
      </div>
    </motion.div>
  );
};

// ==================== UPLOAD MODAL ====================

const UploadModal = ({ cursoId, type, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isYoutube, setIsYoutube] = useState(true);

  const handleUpload = async () => {
    setUploading(true);
    try {
      const formData = new FormData();
      if (titulo) formData.append('titulo', titulo);

      if (type === 'video' && isYoutube) {
        formData.append('tipo', 'video_youtube');
        formData.append('video_url', videoUrl);
      } else {
        formData.append('tipo', type === 'video' ? 'video_mp4' : 'slides');
        formData.append('archivo', file);
      }

      await axiosInstance.post(`/api/talleres/cursos/${cursoId}/contenidos`, formData);
      onDone();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">
            {type === 'video' ? 'Agregar Video' : 'Subir Presentación'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Título</label>
            <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="Título del contenido" />
          </div>

          {type === 'video' && (
            <div className="flex gap-2 mb-2">
              <button onClick={() => setIsYoutube(true)}
                className={`px-3 py-1.5 rounded-lg text-sm ${isYoutube ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500'}`}>
                YouTube URL
              </button>
              <button onClick={() => setIsYoutube(false)}
                className={`px-3 py-1.5 rounded-lg text-sm ${!isYoutube ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500'}`}>
                Subir MP4
              </button>
            </div>
          )}

          {type === 'video' && isYoutube ? (
            <div>
              <label className="block text-sm text-gray-600 mb-1">URL de YouTube</label>
              <input type="url" value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                placeholder="https://youtube.com/watch?v=..." />
            </div>
          ) : (
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                {type === 'video' ? 'Archivo de video (.mp4)' : 'Archivo (.pptx, .ppt, .pdf)'}
              </label>
              <input type="file"
                accept={type === 'video' ? '.mp4,.webm,.mov' : '.pptx,.ppt,.pdf'}
                onChange={e => setFile(e.target.files[0])}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100" />
            </div>
          )}

          <button onClick={handleUpload} disabled={uploading || (!file && !(type === 'video' && isYoutube && videoUrl))}
            className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
            {uploading ? <><Loader2 className="animate-spin" size={16} /> Subiendo...</> : <><Upload size={16} /> Subir</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== QUIZ VIEW ====================

const QuizView = ({ curso, onRefresh, canManage = false, canAnswer = true, progreso = null }) => {
  const [respuestas, setRespuestas] = useState({});
  const [resultado, setResultado] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [preguntas, setPreguntas] = useState((curso.preguntas || []).map((p, idx) => normalizePreguntaForEdit(p, idx)));
  const [newPregunta, setNewPregunta] = useState({ pregunta: '', opciones: ['', '', '', ''], respuesta_correcta_idx: 0 });

  useEffect(() => {
    setPreguntas((curso.preguntas || []).map((p, idx) => normalizePreguntaForEdit(p, idx)));
  }, [curso.preguntas]);

  const handleSubmitQuiz = async () => {
    setSubmitting(true);
    try {
      const { data } = await axiosInstance.post(`/api/talleres/cursos/${curso.id}/quiz/intentar`, { respuestas });
      setResultado(data);
      onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al enviar quiz');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePreguntas = async () => {
    try {
      const payload = (preguntas || []).map((p, idx) => ({
        pregunta: (p.pregunta || '').trim(),
        opciones: (p.opciones || []).map(o => (o || '').trim()),
        respuesta_correcta_idx: Number.isInteger(p.respuesta_correcta_idx) ? p.respuesta_correcta_idx : 0,
        orden: idx + 1,
      }));

      const invalida = payload.some(p =>
        !p.pregunta || p.opciones.length !== 4 || p.opciones.some(o => !o)
      );
      if (invalida) {
        alert('Cada pregunta debe tener enunciado y 4 opciones completas.');
        return;
      }

      await axiosInstance.put(`/api/talleres/cursos/${curso.id}/preguntas`, { preguntas: payload });
      setEditMode(false);
      onRefresh();
    } catch (err) {
      alert('Error al guardar preguntas');
    }
  };

  const addPregunta = () => {
    if (!newPregunta.pregunta.trim()) return;
    setPreguntas([...preguntas, { ...newPregunta, orden: preguntas.length + 1 }]);
    setNewPregunta({ pregunta: '', opciones: ['', '', '', ''], respuesta_correcta_idx: 0 });
  };

  const removePregunta = (idx) => {
    setPreguntas(preguntas.filter((_, i) => i !== idx));
  };

  const bloqueoPorProgreso = canAnswer && progreso && progreso.total_slides > 0 && progreso.slides_vistos < progreso.total_slides;
  const puedeResponder = canAnswer && !bloqueoPorProgreso;

  // Admin: editor de preguntas
  if (canManage && editMode) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Editar Quiz</h3>
          <div className="flex gap-2">
            <button onClick={() => setEditMode(false)} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">Cancelar</button>
            <button onClick={handleSavePreguntas} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
              <Save size={14} /> Guardar
            </button>
          </div>
        </div>

        {/* Preguntas existentes */}
        <div className="space-y-4 mb-6">
          {preguntas.map((p, idx) => (
            <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <p className="font-medium text-gray-800">Pregunta {idx + 1}</p>
                <button onClick={() => removePregunta(idx)} className="text-gray-400 hover:text-red-500"><X size={16} /></button>
              </div>
              <div className="mt-3">
                <input
                  type="text"
                  value={p.pregunta || ''}
                  onChange={(e) => {
                    const next = [...preguntas];
                    next[idx] = { ...next[idx], pregunta: e.target.value };
                    setPreguntas(next);
                  }}
                  placeholder="Texto de la pregunta..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>
              <div className="mt-2 space-y-2">
                {p.opciones.map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name={`correcta-${idx}`}
                      checked={oi === p.respuesta_correcta_idx}
                      onChange={() => {
                        const next = [...preguntas];
                        next[idx] = { ...next[idx], respuesta_correcta_idx: oi };
                        setPreguntas(next);
                      }}
                      className="text-blue-600"
                    />
                    <input
                      type="text"
                      value={opt || ''}
                      onChange={(e) => {
                        const next = [...preguntas];
                        const opciones = [...(next[idx].opciones || ['', '', '', ''])];
                        opciones[oi] = e.target.value;
                        next[idx] = { ...next[idx], opciones };
                        setPreguntas(next);
                      }}
                      placeholder={`Opción ${String.fromCharCode(65 + oi)}`}
                      className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Agregar pregunta */}
        <div className="bg-gray-50 rounded-lg border border-dashed border-gray-300 p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Nueva pregunta</h4>
          <input type="text" value={newPregunta.pregunta} onChange={e => setNewPregunta({ ...newPregunta, pregunta: e.target.value })}
            placeholder="Escribe la pregunta..."
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          <div className="space-y-2 mb-3">
            {newPregunta.opciones.map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <input type="radio" name="correcta" checked={newPregunta.respuesta_correcta_idx === i}
                  onChange={() => setNewPregunta({ ...newPregunta, respuesta_correcta_idx: i })}
                  className="text-blue-600" />
                <input type="text" value={opt} onChange={e => {
                  const opts = [...newPregunta.opciones];
                  opts[i] = e.target.value;
                  setNewPregunta({ ...newPregunta, opciones: opts });
                }}
                  placeholder={`Opción ${String.fromCharCode(65 + i)}`}
                  className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
              </div>
            ))}
          </div>
          <button onClick={addPregunta} className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors">
            <Plus size={14} /> Agregar
          </button>
        </div>
      </div>
    );
  }

  // Resultado del quiz
  if (resultado) {
    return (
      <div className="text-center py-8">
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${resultado.aprobado ? 'bg-emerald-100' : 'bg-red-100'}`}>
          {resultado.aprobado ? <CheckCircle2 className="text-emerald-600" size={40} /> : <AlertTriangle className="text-red-600" size={40} />}
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">{resultado.aprobado ? 'Aprobado' : 'No aprobado'}</h3>
        <p className="text-3xl font-bold text-gray-800 mb-1">{resultado.nota}%</p>
        <p className="text-sm text-gray-500 mb-6">Nota mínima: {curso.nota_aprobatoria}%</p>
        {!resultado.aprobado && (
          <button onClick={() => { setResultado(null); setRespuestas({}); }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            Intentar de nuevo
          </button>
        )}
      </div>
    );
  }

  // Mejor intento previo
  const mejorIntento = curso.mi_mejor_intento;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-800">Quiz — {curso.total_preguntas} preguntas</h3>
          <p className="text-sm text-gray-500">Nota mínima: {curso.nota_aprobatoria}%</p>
        </div>
        {canManage && (
          <button onClick={() => setEditMode(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <Settings size={14} /> Editar preguntas
          </button>
        )}
      </div>

      {mejorIntento && (
        <div className={`rounded-lg p-3 mb-4 ${mejorIntento.aprobado ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          <p className="text-sm">
            Mejor intento: <strong>{mejorIntento.nota}%</strong>
            {mejorIntento.aprobado ? ' — Aprobado' : ` — Necesitas ${curso.nota_aprobatoria}%`}
          </p>
        </div>
      )}

      {curso.total_preguntas === 0 ? (
        <EmptyState icon={CheckCircle2} title="Sin preguntas" description={canManage ? "Configura las preguntas del quiz" : "El quiz aún no está configurado"} />
      ) : (
        <>
          {/* Preguntas para empleados (sin respuestas) */}
          <div className="space-y-6">
            {(curso.preguntas || []).map((p, idx) => (
              <div key={p.id || idx} className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="font-medium text-gray-800 mb-3">{idx + 1}. {p.pregunta}</p>
                <div className="space-y-2">
                  {p.opciones.map((opt, oi) => (
                    <label key={oi} className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                      puedeResponder ? 'cursor-pointer' : 'cursor-default'
                    } ${
                      respuestas[String(p.id)] === oi
                        ? 'bg-blue-50 border border-blue-200'
                        : `${puedeResponder ? 'hover:bg-gray-50' : ''} border border-transparent`
                    }`}>
                      <input type="radio" name={`q-${p.id}`} checked={respuestas[String(p.id)] === oi}
                        onChange={() => {
                          if (!puedeResponder) return;
                          setRespuestas({ ...respuestas, [String(p.id)]: oi });
                        }}
                        disabled={!puedeResponder}
                        className="text-blue-600" />
                      <span className="text-sm text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {bloqueoPorProgreso && (
            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
              Debes completar todo el contenido antes de responder el quiz ({progreso.slides_vistos}/{progreso.total_slides} unidades vistas).
            </div>
          )}

          {canAnswer && (
            <button onClick={handleSubmitQuiz} disabled={!puedeResponder || submitting || Object.keys(respuestas).length < (curso.total_preguntas || 0)}
              className="mt-6 w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
              {submitting ? <><Loader2 className="animate-spin" size={16} /> Enviando...</> : 'Enviar Respuestas'}
            </button>
          )}
        </>
      )}

      {/* Generar quiz con IA — solo admin */}
      {canManage && (
        <QuizIASection cursoId={curso.id} onRefresh={onRefresh} />
      )}
    </div>
  );
};

// ==================== ASIGNACIONES VIEW ====================

const AsignacionesView = ({ curso, onRefresh }) => {
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchAsignaciones = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/api/talleres/cursos/${curso.id}/asignaciones`);
      setAsignaciones(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [curso.id]);

  useEffect(() => { fetchAsignaciones(); }, [fetchAsignaciones]);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar asignación?')) return;
    await axiosInstance.delete(`/api/talleres/asignaciones/${id}`);
    fetchAsignaciones();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-800">{asignaciones.length} asignaciones</h3>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
          <Plus size={14} /> Asignar
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-blue-500" size={24} /></div>
      ) : asignaciones.length === 0 ? (
        <EmptyState icon={Users} title="Sin asignaciones" description="Asigna este curso a empleados o departamentos" />
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium text-gray-600">Empleado</th>
                <th className="text-center px-4 py-2.5 font-medium text-gray-600">Progreso</th>
                <th className="text-center px-4 py-2.5 font-medium text-gray-600">Quiz</th>
                <th className="text-center px-4 py-2.5 font-medium text-gray-600">Estado</th>
                <th className="text-center px-4 py-2.5 font-medium text-gray-600">Fecha límite</th>
                <th className="text-right px-4 py-2.5 font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {asignaciones.map(a => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-800">{a.empleado_nombre}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center gap-2 justify-center">
                      <ProgressBar value={a.porcentaje} className="w-20" />
                      <span className="text-xs text-gray-500">{a.porcentaje}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-600">
                    {a.quiz_intentos_count > 0 ? (
                      <div className="flex flex-col items-center leading-tight">
                        <span>
                          Mejor: <strong>{a.quiz_mejor_nota}%</strong>
                        </span>
                        <span className={a.quiz_mejor_aprobado ? 'text-emerald-600' : 'text-amber-600'}>
                          {a.quiz_mejor_aprobado ? 'Aprobado' : 'No aprobado'}
                        </span>
                        <span className="text-gray-400">Intentos: {a.quiz_intentos_count}</span>
                      </div>
                    ) : (
                      'Sin intentos'
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {a.completado ? <Badge variant="success">Completado</Badge> :
                     a.vencido ? <Badge variant="danger">Vencido</Badge> :
                     a.slides_vistos > 0 ? <Badge variant="info">En progreso</Badge> :
                     <Badge>Sin iniciar</Badge>}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-500 text-xs">
                    {a.deadline ? new Date(a.deadline).toLocaleDateString('es-VE') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleDelete(a.id)} className="text-gray-400 hover:text-red-500"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <AsignarModal cursoId={curso.id} onClose={() => setShowModal(false)} onDone={() => { setShowModal(false); fetchAsignaciones(); }} />
      )}
    </div>
  );
};

// ==================== ASIGNAR MODAL ====================

const AsignarModal = ({ cursoId, onClose, onDone }) => {
  const [mode, setMode] = useState('departamento'); // departamento, individual
  const [departamentos, setDepartamentos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [selectedDept, setSelectedDept] = useState('');
  const [selectedEmps, setSelectedEmps] = useState([]);
  const [deadline, setDeadline] = useState('');
  const [searchEmp, setSearchEmp] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axiosInstance.get('/api/departamentos').then(r => setDepartamentos(r.data)).catch(() => {});
    axiosInstance.get('/api/empleados').then(r => {
      const sorted = (r.data || []).sort((a, b) =>
        getEmpleadoDisplayName(a).localeCompare(getEmpleadoDisplayName(b))
      );
      setEmpleados(sorted);
    }).catch(() => {});
  }, []);

  const filteredEmps = empleados.filter(e =>
    getEmpleadoDisplayName(e).toLowerCase().includes(searchEmp.toLowerCase())
  );

  const handleAsignar = async () => {
    setSubmitting(true);
    try {
      const body = { deadline: deadline || null };
      if (mode === 'departamento') {
        body.departamento_id = parseInt(selectedDept);
      } else {
        body.empleado_ids = selectedEmps;
      }
      await axiosInstance.post(`/api/talleres/cursos/${cursoId}/asignar`, body);
      onDone();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al asignar');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEmp = (id) => {
    setSelectedEmps(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-800">Asignar Curso</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setMode('departamento')}
            className={`px-3 py-1.5 rounded-lg text-sm ${mode === 'departamento' ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500'}`}>
            Por departamento
          </button>
          <button onClick={() => setMode('individual')}
            className={`px-3 py-1.5 rounded-lg text-sm ${mode === 'individual' ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500'}`}>
            Individual
          </button>
        </div>

        {mode === 'departamento' ? (
          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-1">Departamento</label>
            <select value={selectedDept} onChange={e => setSelectedDept(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
              <option value="">Seleccionar...</option>
              {departamentos.map(d => (
                <option key={d.id} value={d.id}>{d.nombre}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-1">Empleados</label>
            <input type="text" value={searchEmp} onChange={e => setSearchEmp(e.target.value)}
              placeholder="Buscar empleado..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
              {filteredEmps.map(e => (
                <label key={e.id} className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 ${selectedEmps.includes(e.id) ? 'bg-blue-50' : ''}`}>
                  <input type="checkbox" checked={selectedEmps.includes(e.id)} onChange={() => toggleEmp(e.id)} className="text-blue-600" />
                  <span className="text-sm text-gray-700">{getEmpleadoDisplayName(e)}</span>
                </label>
              ))}
            </div>
            {selectedEmps.length > 0 && (
              <p className="text-xs text-blue-600 mt-1">{selectedEmps.length} seleccionados</p>
            )}
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm text-gray-600 mb-1">Fecha límite (opcional)</label>
          <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
        </div>

        <button onClick={handleAsignar}
          disabled={submitting || (mode === 'departamento' ? !selectedDept : selectedEmps.length === 0)}
          className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
          {submitting ? <><Loader2 className="animate-spin" size={16} /> Asignando...</> : <><Users size={16} /> Asignar</>}
        </button>
      </div>
    </div>
  );
};

// ==================== CREAR CURSO ====================

const CursoFormView = ({ onBack, onCreated }) => {
  const [form, setForm] = useState({
    titulo: '', descripcion: '', duracion_minutos: '', requiere_quiz: false,
    nota_aprobatoria: 70, vigencia_dias: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titulo.trim()) return;
    setSaving(true);
    try {
      const body = {
        ...form,
        duracion_minutos: form.duracion_minutos ? parseInt(form.duracion_minutos) : null,
        vigencia_dias: form.vigencia_dias ? parseInt(form.vigencia_dias) : null,
      };
      const { data } = await axiosInstance.post('/api/talleres/cursos', body);
      onCreated(data);
    } catch (err) {
      alert('Error al crear curso');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors">
        <ChevronLeft size={18} /> Volver
      </button>

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Nuevo Curso</h1>

      <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
          <input type="text" value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            placeholder="Ej: Seguridad Industrial — Uso de EPP" required />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
          <textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })}
            rows={3}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
            placeholder="Descripción del curso..." />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duración (minutos)</label>
            <input type="number" value={form.duracion_minutos} onChange={e => setForm({ ...form, duracion_minutos: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="30" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vigencia (días)</label>
            <input type="number" value={form.vigencia_dias} onChange={e => setForm({ ...form, vigencia_dias: e.target.value })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="365 (vacío = no vence)" />
          </div>
        </div>

        <div className="flex items-center gap-4 py-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.requiere_quiz} onChange={e => setForm({ ...form, requiere_quiz: e.target.checked })} className="text-blue-600 rounded" />
            <span className="text-sm text-gray-700">Requiere quiz para completar</span>
          </label>
        </div>

        {form.requiere_quiz && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nota aprobatoria (%)</label>
            <input type="number" min="1" max="100" value={form.nota_aprobatoria} onChange={e => setForm({ ...form, nota_aprobatoria: parseInt(e.target.value) || 70 })}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
          </div>
        )}

        <button type="submit" disabled={saving || !form.titulo.trim()}
          className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors">
          {saving ? <><Loader2 className="animate-spin" size={16} /> Creando...</> : <><Plus size={16} /> Crear Curso</>}
        </button>
      </form>
    </motion.div>
  );
};

// ==================== IA VIEW ====================

// ==================== SINOPSIS IA (en tab Contenido) ====================

const SinopsisSection = ({ curso, canManage, onRefresh }) => {
  const [texto, setTexto] = useState(curso.sinopsis || '');
  const [editing, setEditing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setTexto(curso.sinopsis || ''); }, [curso.sinopsis]);

  const handleGenerar = async () => {
    setGenerating(true);
    try {
      const { data } = await axiosInstance.post(`/api/talleres/cursos/${curso.id}/ia/resumen`);
      setTexto(data.sinopsis);
      setDirty(true);
      setEditing(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al generar sinopsis');
    } finally {
      setGenerating(false);
    }
  };

  const handleGuardar = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/api/talleres/cursos/${curso.id}/sinopsis`, { sinopsis: texto });
      setDirty(false);
      setEditing(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al guardar sinopsis');
    } finally {
      setSaving(false);
    }
  };

  const renderSinopsis = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let isFirst = true;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      if (isFirst) {
        elements.push(<h2 key={i} style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem', lineHeight: 1.3 }}>{line}</h2>);
        isFirst = false;
      } else if (
        i + 1 < lines.length && lines[i + 1]?.trim() &&
        line.length < 40 && !line.endsWith('.') &&
        elements.length > 1
      ) {
        elements.push(<h3 key={i} style={{ fontSize: '1.1rem', fontWeight: 600, color: '#475569', marginTop: '1.25rem', marginBottom: '0.5rem' }}>{line}</h3>);
      } else if (line.length < 60 && !line.endsWith('.') && elements.length > 2) {
        elements.push(<p key={i} style={{ fontSize: '1rem', color: '#64748b', lineHeight: 1.6, paddingLeft: '0.25rem' }}>{line}</p>);
      } else {
        elements.push(<p key={i} style={{ fontSize: '1.05rem', color: '#334155', lineHeight: 1.7, marginBottom: '0.5rem' }}>{line}</p>);
      }
    }
    return elements;
  };

  // Sin sinopsis guardada y no es admin → no mostrar nada
  if (!texto && !canManage) return null;

  return (
    <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
      {/* Header con acciones admin */}
      {canManage && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 500 }}>Sinopsis</p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {texto && !editing && (
              <button onClick={() => setEditing(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', fontSize: '0.8rem', color: '#64748b', background: '#f1f5f9', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>
                <Pencil size={14} /> Editar
              </button>
            )}
            {curso.contenidos?.length > 0 && (
              <button onClick={handleGenerar} disabled={generating}
                style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.375rem 0.75rem', fontSize: '0.8rem', color: '#7c3aed', background: '#f5f3ff', border: 'none', borderRadius: '0.5rem', cursor: generating ? 'wait' : 'pointer', opacity: generating ? 0.6 : 1 }}>
                {generating ? <Loader2 size={14} className="animate-spin" /> : <FaMagic size={12} />}
                {generating ? 'Generando...' : texto ? 'Regenerar con IA' : 'Generar con IA'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modo edicion */}
      {editing && canManage ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <textarea
            value={texto}
            onChange={(e) => { setTexto(e.target.value); setDirty(true); }}
            rows={8}
            style={{ width: '100%', padding: '1rem', fontSize: '1rem', lineHeight: 1.7, color: '#334155', border: '2px solid #c4b5fd', borderRadius: '0.75rem', resize: 'vertical', outline: 'none', fontFamily: 'inherit' }}
            placeholder="Escribe la sinopsis del curso..."
          />
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
            <button onClick={() => { setEditing(false); setTexto(curso.sinopsis || ''); setDirty(false); }}
              style={{ padding: '0.5rem 1rem', fontSize: '0.85rem', color: '#64748b', background: '#f1f5f9', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>
              Cancelar
            </button>
            <button onClick={handleGuardar} disabled={saving || !dirty}
              style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.5rem 1rem', fontSize: '0.85rem', color: 'white', background: dirty ? '#7c3aed' : '#a5b4fc', border: 'none', borderRadius: '0.5rem', cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Guardar
            </button>
          </div>
        </motion.div>
      ) : texto ? (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          style={{ padding: '1.5rem 0.5rem' }}>
          {renderSinopsis(texto)}
        </motion.div>
      ) : canManage ? (
        <p style={{ color: '#94a3b8', fontSize: '0.9rem', fontStyle: 'italic' }}>
          Sin sinopsis. Usa el boton de IA para generar una automaticamente.
        </p>
      ) : null}
    </div>
  );
};

// ==================== QUIZ IA (en tab Quiz) ====================

const QuizIASection = ({ cursoId, onRefresh }) => {
  const [quizPreview, setQuizPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [numPreguntas, setNumPreguntas] = useState(5);
  const [saved, setSaved] = useState(false);

  const handleGenerar = async () => {
    setLoading(true);
    setQuizPreview(null);
    setSaved(false);
    try {
      const { data } = await axiosInstance.post(`/api/talleres/cursos/${cursoId}/ia/quiz`, { num_preguntas: numPreguntas });
      setQuizPreview(data.preguntas);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al generar quiz');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await axiosInstance.put(`/api/talleres/cursos/${cursoId}/preguntas`, { preguntas: quizPreview });
      setSaved(true);
      onRefresh();
    } catch (err) {
      alert('Error al guardar preguntas');
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Sparkles size={18} className="text-blue-500" /> Generar quiz con IA
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">Crea preguntas automaticamente desde el contenido de la presentacion</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Preguntas:</label>
            <select value={numPreguntas} onChange={e => setNumPreguntas(parseInt(e.target.value))}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
              {[3, 5, 7, 10, 15].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <button onClick={handleGenerar} disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {loading ? <><Loader2 className="animate-spin" size={16} /> Generando...</> : <><Sparkles size={16} /> Generar Quiz</>}
          </button>
        </div>
      </div>

      {quizPreview && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="space-y-4 mb-4">
            {quizPreview.map((p, idx) => (
              <div key={idx} className="bg-white rounded-lg border border-gray-200 p-4">
                <p className="font-medium text-gray-800 mb-2">{idx + 1}. {p.pregunta}</p>
                <div className="space-y-1">
                  {p.opciones.map((opt, oi) => (
                    <div key={oi} className={`text-sm px-3 py-1.5 rounded ${oi === p.respuesta_correcta_idx ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600'}`}>
                      {String.fromCharCode(65 + oi)}. {opt}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {!saved ? (
            <div className="flex items-center gap-3">
              <button onClick={handleSave}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors">
                <Save size={16} /> Guardar como quiz del curso
              </button>
              <button onClick={handleGenerar}
                className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-50 transition-colors">
                <Sparkles size={16} /> Regenerar
              </button>
              <p className="text-xs text-gray-400">Esto reemplazara las preguntas existentes del quiz</p>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
              <CheckCircle2 size={16} /> Preguntas guardadas correctamente
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};

export default TalleresView;
